import { lazy, Suspense, useMemo, useState } from "react";
import { useUserDataFlattened } from "../../../modules/user/stores";
import { useQueuedPopup } from "../../../hooks/useQueuedPopup";
import { POPUP_CONFIGS } from "@/config/popupConfig";
import {
  useActivityPopupPriority,
  useIsHomeDisplayReady,
} from "@/modules/homeDisplay/hooks";
import { ActivityType, TimingType } from "@/modules/homeDisplay/types";
import type { RedPacketPopupResult } from "./useRedPacketPopup.types";
import { ROUTES } from "../../../utils/navigation";

const RedPacketRain = lazy(() =>
  import("../components/RedPacketRain/RedPacketRain").then(m => ({
    default: m.RedPacketRain,
  }))
);

const RedPacketRainContent: React.FC<{ onComplete: () => void }> = ({
  onComplete,
}) => {
  const [visible, setVisible] = useState(true);

  const handleClose = () => {
    setVisible(false);
    onComplete();
  };

  return (
    <Suspense fallback={null}>
      <RedPacketRain visible={visible} onClose={handleClose} />
    </Suspense>
  );
};

const RED_PACKET_CONFIG = POPUP_CONFIGS.RED_PACKET;

export const useRedPacketPopup = (): RedPacketPopupResult => {
  const { data: userInfo, isAuthenticated } = useUserDataFlattened();
  const isConfigReady = useIsHomeDisplayReady();

  // 获取活动配置中的 priority，不存在则使用默认值
  const priority = useActivityPopupPriority(
    ActivityType.RED_PACKET_RAIN,
    RED_PACKET_CONFIG.priority,
    TimingType.ACTIVITY_QUALIFICATION
  );

  const redStartMs = userInfo?.redpacket?.siteConf?.startTime;
  const redEndMs = userInfo?.redpacket?.siteConf?.endTime;

  const redDecide = useMemo(() => {
    const now = Date.now();
    if (redStartMs && redEndMs) {
      const isCountingToEnd = now >= redStartMs;
      return {
        targetMs: isCountingToEnd ? redEndMs : redStartMs,
        isOpen:
          isCountingToEnd &&
          (userInfo?.redPacketReceivable || !isAuthenticated),
      };
    }
    if (redStartMs) return { targetMs: redStartMs, isOpen: false };
    if (redEndMs) return { targetMs: redEndMs, isOpen: true };
    return { targetMs: now, isOpen: false };
  }, [redStartMs, redEndMs, userInfo?.redPacketReceivable, isAuthenticated]);

  const shouldShow = !!userInfo?.redpacket;

  const { trigger } = useQueuedPopup({
    popupId: RED_PACKET_CONFIG.id,
    priority,
    shouldShow: () => isConfigReady && !!userInfo?.redpacket,
    renderContent: onComplete => (
      <RedPacketRainContent onComplete={onComplete} />
    ),
    showOnce: true,
    allowManualTrigger: true,
    autoTrigger: false,
    allowedPaths: [ROUTES.HOME, ROUTES.HOME_ALT, "/", ROUTES.EVENT_CENTER, ROUTES.LOGIN, ROUTES.REGISTER],
    dependencies: [isConfigReady, userInfo?.redpacket],
    timingType: TimingType.ACTIVITY_QUALIFICATION,
  });

  const remainingMs = Math.max(0, redDecide.targetMs - Date.now());

  return {
    shouldShow,
    trigger,
    remainingMs,
    isOpen: redDecide.isOpen,
  };
};
